import { styled } from '@mui/material';
import { forwardRef, useEffect, useRef } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { useApp } from 'contexts/app';
import {
    BottomBar,
    Box,
    Button,
    Checkbox,
    Divider,
    Dropdown,
    Icon,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from 'shell/components';

const CriteriaStep = forwardRef(
    (
        {
            groupFormation,
            surveys,
            form,
            setForm,
            errors,
            getCriterionImportance,
            getCriterionMixture,
            onChange,
            onClick,
            onClickBack,
            onDragEnd,
            onSelect,
            onSubmit,
            onSubmitDraft,
        },
        ref
    ) => {
        const { studentAccountUsers } = useApp();
        const scrollRef = useRef(null);

        const hasChanges = groupFormation?.modified_on !== form.modified_on;
        const isPublished = Boolean(groupFormation?.status === 'published' && surveys.length);

        useEffect(() => {
            if (Boolean(errors.mode) && scrollRef.current) scrollRef.current.scrollIntoView();
        }, [errors]);

        return (
            <CustomBox ref={ref}>
                <Stack divider={<Divider />} spacing={6}>
                    <Stack spacing={7}>
                        <Stack direction={'row'} justifyContent={'space-between'}>
                            <IconButton kind={'arrowLeft'} size={'medium'} variant={'square'} onClick={onClickBack} />
                            {/* <Button variant={'secondary'}>
                                View as a Student
                            </Button> */}
                        </Stack>
                        <Stack spacing={1}>
                            <Text variant={'h6'} weight={'semibold'}>
                                Create Teams Based on a Specific Set of Criteria
                            </Text>
                            <Text variant={'p1'}>
                                Form student teams based on pre-existing criteria or create your own. A questionnaire
                                will be sent out to the students if there are any criteria that needs additional
                                information.
                            </Text>
                        </Stack>
                        <Stack alignItems={'center'} direction={'row'} spacing={10}>
                            <Text variant={'p1'}>
                                Class size:{' '}
                                <Text color={'primary'} component={'span'}>
                                    {studentAccountUsers.length} students
                                </Text>
                            </Text>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text variant={'p1'}>
                                    Student Profile Completion:{' '}
                                    <Text component={'span'}>
                                        {0}/{studentAccountUsers.length}
                                    </Text>
                                </Text>
                                <Tooltip
                                    content={
                                        'The number of students in the class that has finished their profile set-up. Instructors can create groups based on specific criteria by using information from a student’s profile. Groups created with this option will be more diverse when the number of students that complete their profile is higher.'
                                    }
                                    type={'info'}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                    <Stack>
                        <CustomStack alignItems={'center'} direction={'row'} spacing={4}>
                            <Text variant={'p1'} weight={'semibold'}>
                                Criteria
                            </Text>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text variant={'p1'} weight={'semibold'}>
                                    Importance
                                </Text>
                                <Tooltip
                                    content={
                                        'Importance allows the algorithm to determine which criteria to prioritize when forming groups.'
                                    }
                                    type={'info'}
                                />
                            </Stack>
                            <Text variant={'p1'} weight={'semibold'}>
                                Place students with the same answers in...
                            </Text>
                            <Text variant={'p1'} weight={'semibold'}>
                                Show/Hide
                            </Text>
                        </CustomStack>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId={'criteria-droppable'} isDropDisabled={isPublished}>
                                {(provided) => (
                                    <Stack ref={provided.innerRef} {...provided.droppableProps}>
                                        {form.criteria.map((criterion, index) => {
                                            const isDefault = [
                                                'time-zone',
                                                'country',
                                                'gender',
                                                'english-proficiency',
                                            ].includes(criterion.id);
                                            return (
                                                <Draggable
                                                    key={criterion.id}
                                                    draggableId={criterion.id}
                                                    index={index}
                                                    isDragDisabled={isPublished}
                                                >
                                                    {(provided) => (
                                                        <CustomStack
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            alignItems={'center'}
                                                            direction={'row'}
                                                            spacing={4}
                                                        >
                                                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                                                <Icon
                                                                    sx={{
                                                                        ...(isPublished && {
                                                                            color: (theme) =>
                                                                                theme.palette.ensightful.neutral[400],
                                                                        }),
                                                                    }}
                                                                    kind={'reorder'}
                                                                />
                                                                <Text variant={'p2'}>{criterion.name}</Text>
                                                            </Stack>
                                                            <Text variant={'p2'}>
                                                                {getCriterionImportance(criterion.weight)}
                                                            </Text>
                                                            <Text variant={'p2'}>
                                                                {getCriterionMixture(criterion.mixture)}
                                                            </Text>
                                                            <Stack
                                                                sx={{ pl: '18px' }}
                                                                alignItems={'center'}
                                                                direction={'row'}
                                                            >
                                                                <Checkbox
                                                                    checked={criterion.show}
                                                                    disabled={isPublished}
                                                                    onChange={(_, checked) =>
                                                                        setForm((current) => ({
                                                                            ...current,
                                                                            criteria: current.criteria.map((c) => {
                                                                                return c.id === criterion.id
                                                                                    ? {
                                                                                          ...c,
                                                                                          show: checked,
                                                                                      }
                                                                                    : c;
                                                                            }),
                                                                            modified_on: new Date().getTime(),
                                                                        }))
                                                                    }
                                                                />
                                                                <Dropdown
                                                                    id={`criterion-${criterion.id}-dropdown`}
                                                                    options={[
                                                                        { key: 'edit', value: 'Edit' },
                                                                        {
                                                                            key: 'delete',
                                                                            value: 'Delete',
                                                                            disabled: isDefault || isPublished,
                                                                        },
                                                                    ]}
                                                                    rootComponent={
                                                                        <IconButton
                                                                            kind={'moreHorizontal'}
                                                                            size={'small'}
                                                                            variant={'square'}
                                                                        />
                                                                    }
                                                                    onSelect={(key) => onSelect(key, criterion)}
                                                                />
                                                            </Stack>
                                                        </CustomStack>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </Stack>
                                )}
                            </Droppable>
                        </DragDropContext>
                        <Button
                            sx={{ alignSelf: 'flex-start' }}
                            disabled={isPublished}
                            startIcon={<Icon kind={'add'} />}
                            variant={'tertiary'}
                            onClick={onClick}
                        >
                            Add Criterion
                        </Button>
                    </Stack>
                    <Stack spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            I want to...
                            <Text color={'error'} component={'span'} variant={'p1'}>
                                *
                            </Text>
                        </Text>
                        <RadioGroup
                            sx={{ gap: 2 }}
                            value={form.mode}
                            onChange={(e) => onChange('mode', e.target.value)}
                        >
                            <Radio
                                error={Boolean(errors.mode)}
                                value={'set-groups'}
                                label={
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        Divide students into
                                        <CustomTextInput
                                            error={Boolean(errors['set-groups-value'])}
                                            helperText={errors['set-groups-value']}
                                            placeholder={'e.g. 4'}
                                            value={form['set-groups-value']}
                                            onChange={(e) => onChange('set-groups-value', e.target.value)}
                                        />
                                        groups
                                    </Stack>
                                }
                            />
                            <Radio
                                error={Boolean(errors.mode)}
                                value={'set-students-per-group'}
                                label={
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        Divide students into groups of
                                        <CustomTextInput
                                            error={Boolean(errors['set-students-per-group-value'])}
                                            helperText={errors['set-students-per-group-value']}
                                            placeholder={'e.g. 4'}
                                            value={form['set-students-per-group-value']}
                                            onChange={(e) => onChange('set-students-per-group-value', e.target.value)}
                                        />
                                    </Stack>
                                }
                            />
                        </RadioGroup>
                        <Box ref={scrollRef} />
                    </Stack>
                </Stack>
                <BottomBar>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        {hasChanges ? (
                            <Text color={'error'} variant={'p2'}>
                                Caution - You have unsaved changes!
                            </Text>
                        ) : (
                            <Text variant={'p2'}>All changes saved.</Text>
                        )}
                        <Stack direction={'row'} spacing={4}>
                            <Button disabled={!hasChanges} variant={'tertiary'} onClick={onSubmitDraft}>
                                Save as Draft
                            </Button>
                            <Button onClick={onSubmit}>
                                {groupFormation ? 'Update and Send' : 'Create Questionnaire'}
                            </Button>
                        </Stack>
                    </Stack>
                </BottomBar>
            </CustomBox>
        );
    }
);

const CustomBox = styled(Box)(({ theme }) => {
    return {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(20),
        paddingBottom: theme.spacing(20),
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        '& > *:nth-child(1)': {
            minWidth: 'calc(100% / 3)',
            maxWidth: 'calc(100% / 3)',
            paddingRight: theme.spacing(3),
        },
        '& > *:nth-child(2)': {
            flex: 0.5,
        },
        '& > *:nth-child(3)': {
            flex: 0.5,
        },
        '& > *:nth-child(4)': {
            width: 82,
        },
    };
});

const CustomTextInput = styled(TextInput)(() => ({
    width: '8rem',
}));

export default CriteriaStep;
